import { useMemo, useState } from 'react';
import { useUpdateEffect } from 'react-use';

import { isSignedParticipantDeprecated } from '@app/libs/documents/isSignedParticipant';
import Host from '@app/store/Host';

import isSignableInPersonSigner from '../../lib/isSignableInPersonSigner';
import { InPersonDetail, InPersonSigner, InPersonSignerWithSigningStatus } from '../../types';

export default function useParticipantSelect({
  inPersonDetail: { inPersonSigners, signings, currentSigningOrder },
  currentHost,
}: {
  inPersonDetail: Pick<InPersonDetail, 'inPersonSigners' | 'signings' | 'currentSigningOrder'>;
  currentHost: Host | undefined;
}) {
  const currentSigners: InPersonSigner[] = useMemo(
    () =>
      inPersonSigners.filter(({ host }) => currentHost && host.dataLabel === currentHost.dataLabel),
    [currentHost, inPersonSigners]
  );

  const currentSignersWithSigningStatus = useMemo(
    () =>
      currentSigners.map<InPersonSignerWithSigningStatus>((p) => ({
        ...p,
        hasSigned: isSignedParticipantDeprecated(signings, p),
      })),
    [currentSigners, signings]
  );

  const [selectedSigner, setSelectedSigner] = useState<InPersonSignerWithSigningStatus | undefined>(
    () =>
      currentSignersWithSigningStatus.find((signer) =>
        isSignableInPersonSigner(signer, currentSigningOrder)
      )
  );

  useUpdateEffect(() => {
    setSelectedSigner(
      currentSignersWithSigningStatus.find((signer) =>
        isSignableInPersonSigner(signer, currentSigningOrder)
      )
    );
  }, [currentSignersWithSigningStatus]);

  const setSelectedSignerIfValid = (signerId: string) => {
    const signer = currentSignersWithSigningStatus.find((s) => s.participantId === signerId);

    if (isSignableInPersonSigner(signer, currentSigningOrder)) {
      setSelectedSigner(signer);
      return true;
    }
    return false;
  };

  return {
    selectedSigner,
    setSelectedSignerIfValid,
    currentSignersWithSigningStatus,
    canStartInPersonSession: !!selectedSigner,
  };
}
